/* eslint-disable camelcase */
/* global Conviva */
// @flow

import type { Store } from 'redux';
import { isEmpty } from 'lodash';
import { isLiveLinearSourceType } from 'Common/models/franchise';
import type { State, BaseAction, ThunkAction, Dispatch } from 'Common/types';
import {
  AD_BREAK_TYPE_PREROLL,
  AD_BREAK_TYPE_MIDROLL,
  AD_BREAK_TYPE_POSTROLL,
  AD_BREAK_TYPE_LIVE,
} from './AnalyticsConstants';
import BaseCastAnalytics from './BaseCastAnalytics';

const TAG = 'CONVIVA[CAST]';
const BITRATE_UPDATE_INTERVAL = 2000;

declare var Conviva: Object;

class ConvivaCastAnalytics extends BaseCastAnalytics {
  convivaVideoAnalytics: ?Object;
  podIndex: number;
  _updateBitrateIntervalId: IntervalID;

  constructor(playerManager: Object, store: Store<State, BaseAction | ThunkAction, Dispatch>) {
    super(playerManager, store);
    this.convivaVideoAnalytics = null;
    this.podIndex = -1; // default for live & postroll
  }

  get enabled() {
    return this.customAttributes?.conviva_enabled;
  }

  get streamType() {
    return isLiveLinearSourceType(this.sourceType)
      ? Conviva.Constants.StreamType.LIVE
      : Conviva.Constants.StreamType.VOD; // UNKNOWN/LIVE/VOD
  }

  get contentLengthStr() {
    return String(this.contentLength);
  }

  _initialize() {
    if (this.isInitialized) return;

    if (Conviva) {
      const customerKey = this.customAttributes?.conviva_customer_key; // '24c1108c797ecde51bd8e288e157e787c1b9ee6c';
      const gatewayUrl = this.customAttributes?.conviva_service_url; // 'https://echostar-dany.testonly.conviva.com';
      const settings = {};

      settings[Conviva.Constants.GATEWAY_URL] = gatewayUrl;
      settings[Conviva.Constants.LOG_LEVEL] = Conviva.Constants.LogLevel.DEBUG;

      Conviva.Analytics.init(customerKey, null /* settings */); // the settings argument is disabled in the production environment
      this.convivaVideoAnalytics = Conviva.Analytics.buildVideoAnalytics();
      if (this.convivaVideoAnalytics) {
        this.convivaVideoAnalytics.setPlayer(this.playerManager);
        const contentInfo = this._getContentInfo();

        if (contentInfo) {
          // $FlowFixMe
          this.convivaVideoAnalytics.reportPlaybackRequested(contentInfo);
          this.isInitialized = true;
          this._updateBitrateIntervalId = setInterval(() => {
            // $FlowFixMe
            this.convivaVideoAnalytics.reportPlaybackMetric(
              Conviva.Constants.Playback.BITRATE,
              this.streamBandwidth,
            );
          }, BITRATE_UPDATE_INTERVAL);
        }
      }
    }
  }

  release() {
    if (Conviva && this.convivaVideoAnalytics) {
      this.convivaVideoAnalytics.reportPlaybackEnded();
      // $FlowFixMe
      this.convivaVideoAnalytics.release();
      this.convivaVideoAnalytics = null;
      Conviva.Analytics.release();
      this.isInitialized = false;
      this.podIndex = -1;
      this.isLlAdPlaying = false;
      clearInterval(this._updateBitrateIntervalId);
      this.logAnalytics(TAG, ' >>>>>  ', 'release');
    }
  }

  onCafEvent(event: Object) {
    this.logAnalytics(TAG, 'Player event: ', event.type, event);

    switch (event.type) {
      case 'REQUEST_LOAD': {
        const { requestData } = event;
        if (this.isLiveLinearAdStart(requestData)) {
          this._onAdBreakStart(requestData);
        } else if (this.isLiveLinearAdEnd(requestData)) {
          this._onAdBreakEnd();
        }
        event.handleInSuper = true;
        break;
      }
      case 'PLAYER_LOADING': {
        if (isLiveLinearSourceType(this.sourceType)) {
          this.checkQvtAndStart(() => {
            this._initialize();
          });
        } else {
          this._initialize();
        }
        break;
      }
      case 'PLAYER_LOAD_COMPLETE': {
        this._updateContentInfo(event);
        break;
      }
      case 'BREAK_STARTED':
        this._onAdBreakStart(event);
        break;
      case 'BREAK_ENDED':
        this._onAdBreakEnd();
        break;
      case 'BITRATE_CHANGED': {
        if (this.convivaVideoAnalytics)
          this.convivaVideoAnalytics.reportPlaybackMetric(
            Conviva.Constants.Playback.BITRATE,
            this.streamBandwidth,
          );
        break;
      }
      case 'ERROR': {
        if (!this.convivaVideoAnalytics) return;

        const errorMessage =
          event.error && Object.keys(event.error).length !== 0
            ? JSON.stringify(event.error)
            : `${event.type}:  ${event?.detailedErrorCode || 'N/A'}`;
        this.convivaVideoAnalytics.reportPlaybackFailed(errorMessage);
        event.handleInSuper = true;
        break;
      }

      default:
        event.handleInSuper = true;
        break;
    }
    return event;
  }

  onCafShutdown() {
    this.release();
  }

  _getAdBreakType(adBreakId?: string) {
    const type = this.getAdBreakType(adBreakId);
    switch (type) {
      case AD_BREAK_TYPE_PREROLL: {
        this.podIndex = 0;
        break;
      }
      case AD_BREAK_TYPE_MIDROLL: {
        this.podIndex += 1;
        break;
      }
      case AD_BREAK_TYPE_POSTROLL:
      case AD_BREAK_TYPE_LIVE: {
        this.podIndex = -1;
        break;
      }
      default:
        this.podIndex = 0;
    }
    return type;
  }

  _getAdBreakTotalDuration(breakId?: string) {
    let duration = 0;

    if (!isLiveLinearSourceType(this.sourceType)) {
      const currentAdClipIds = this.breakManager.getBreakById(breakId)?.breakClipIds;
      const allAdClips = this.breakManager.getBreakClips();

      if (!isEmpty(currentAdClipIds) && !isEmpty(allAdClips)) {
        const filteredAdClips = allAdClips.filter(
          clip => currentAdClipIds.find(c => c === clip.id) !== undefined,
        );
        duration = filteredAdClips.reduce((acc, current) => {
          acc += current.duration;
          return acc;
        }, 0);
      }
    } else {
      duration = this.getLiveLinearAdBrakeTotalDuration();
    }
    return duration;
  }

  _getAdBreak(event: Object) {
    const breakId = this.getBreakId(event);
    if (!breakId && !isLiveLinearSourceType(this.sourceType)) return null;

    const adBreak = {};

    adBreak[Conviva.Constants.POD_POSITION] = this._getAdBreakType(breakId);
    adBreak[Conviva.Constants.POD_DURATION] = this._getAdBreakTotalDuration(breakId);
    adBreak[Conviva.Constants.POD_INDEX] = this.podIndex;
    return adBreak;
  }

  _onAdBreakStart(event: Object) {
    if (!this.convivaVideoAnalytics) return;

    this.convivaVideoAnalytics.reportAdBreakStarted(
      Conviva.Constants.AdType.CLIENT_SIDE,
      Conviva.Constants.AdPlayer.CONTENT,
      this._getAdBreak(event),
    );
  }

  _onAdBreakEnd = () =>
    this.convivaVideoAnalytics && this.convivaVideoAnalytics.reportAdBreakEnded();

  _updateContentInfo(event: Object) {
    if (
      this.convivaVideoAnalytics &&
      (this.isLiveLinearAdStart(event) ||
        this.isLiveLinearAdProceedPlaying(event) ||
        this.isLiveLinearAdEnd(event))
    ) {
      const contentInfo = this._getContentInfo();
      // $FlowFixMe
      this.convivaVideoAnalytics.setContentInfo(contentInfo);
    }
  }

  _getCustomTagContentInfo() {
    const customInfo = {};

    customInfo.abTest = this.abTest;
    customInfo.channel = this.channelOrNetworkName;
    customInfo.contentID = this.contentId;
    customInfo.connectionType = this.connectionType;
    customInfo.carrier = this.carrierName;
    customInfo.contentType = this.contentType;
    customInfo.drm = this.drmName;
    customInfo.customDma = this.customDma;
    customInfo.quality = this.quality;
    customInfo.duration = this.contentLengthStr;
    customInfo.genre = this.contentGenresString;
    customInfo.episodeName = this.episodeTitle;
    customInfo.show = this.franchiseName;
    customInfo.releaseDate = this.releaseDate;
    customInfo.rating = this.rating;
    customInfo.playerVersion = this.playerVersion;
    customInfo.partner = this.partnerId;
    customInfo.protocol = this.streamProtocol;
    customInfo.cdnName = this.cdnName;

    return customInfo;
  }

  _getContentInfo() {
    const contentInfo = {};

    contentInfo[Conviva.Constants.DEFAULT_RESOURCE] = this.cdnName;
    contentInfo[Conviva.Constants.STREAM_URL] = this.castClipUrl;
    contentInfo[Conviva.Constants.PLAYER_NAME] = this.playerName;
    contentInfo[Conviva.Constants.VIEWER_ID] = this.viewerId;
    contentInfo[Conviva.Constants.ASSET_NAME] = this.assetName;
    contentInfo[Conviva.Constants.IS_LIVE] = this.streamType;
    contentInfo[Conviva.Constants.DURATION] = this.contentLengthStr;
    contentInfo[Conviva.Constants.ENCODED_FRAMERATE] = this.encodedFramerate;

    return { ...contentInfo, ...this._getCustomTagContentInfo() };
  }

  onPlaybackFinished() {
    if (!this.isLlAdPlaying) {
      this.release();
    }
  }
}

export default ConvivaCastAnalytics;
